import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Clock3, Timer, Users } from "lucide-react";
import { papi, errText, localISO } from "../portalApi";
import { Card, EmptyState, PageHeader, Spinner, StatusBadge, fmtTime, inputCls } from "../ui";

const shiftDay = (dateStr, days) => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return localISO(d);
};

export default function Reports() {
  const [date, setDate] = useState(() => localISO(new Date()));
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await papi.get("/reports/day", { params: { date } });
      setData(r.data);
    } catch (err) {
      toast.error(errText(err, "Could not load the day report."));
    }
  }, [date]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  return (
    <div data-testid="reports-page">
      <PageHeader title="Day Reports" sub="Organisation-wide attendance and timesheet activity for any date.">
        <div className="flex items-center gap-2">
          <button onClick={() => setDate(shiftDay(date, -1))} className="rounded-lg border border-navy-900/15 bg-white p-2 text-navy-900 transition-colors hover:border-gold" aria-label="Previous day" data-testid="report-prev">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <input type="date" className={inputCls} value={date} onChange={(e) => e.target.value && setDate(e.target.value)} data-testid="report-date" />
          <button onClick={() => setDate(shiftDay(date, 1))} className="rounded-lg border border-navy-900/15 bg-white p-2 text-navy-900 transition-colors hover:border-gold" aria-label="Next day" data-testid="report-next">
            <ChevronRight className="h-4 w-4" />
          </button>
          <button onClick={() => setDate(localISO(new Date()))} className="rounded-lg border border-navy-900/15 bg-white px-3 py-2 text-xs font-semibold text-navy-900 transition-colors hover:border-gold" data-testid="report-today">
            Today
          </button>
        </div>
      </PageHeader>

      {!data ? (
        <Spinner />
      ) : (
        <>
          {data.holiday && (
            <p className="mb-4 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3 text-sm font-semibold text-navy-900" data-testid="report-holiday-banner">
              Company holiday: {data.holiday}
            </p>
          )}

          <div className="mb-6 grid gap-4 sm:grid-cols-3" data-testid="report-summary">
            <Card>
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><Users className="h-4 w-4 text-gold-dark" aria-hidden /> Headcount</p>
              <p className="mt-2 font-serif text-3xl text-navy-900" data-testid="report-headcount">{data.summary.headcount}</p>
              <p className="mt-1 text-xs text-slate-500" data-testid="report-status-chips">
                Present {data.summary.counts.Present || 0} · WFH {data.summary.counts["Work From Home"] || 0} · Leave {data.summary.counts.Leave || 0} · Weekly Off {data.summary.counts["Weekly Off"] || 0} · Not Marked {data.summary.counts["Not Marked"] || 0}
              </p>
            </Card>
            <Card>
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><Clock3 className="h-4 w-4 text-gold-dark" aria-hidden /> Attendance Hours</p>
              <p className="mt-2 font-serif text-3xl text-navy-900" data-testid="report-att-hours">{data.summary.attendanceHours}h</p>
              <p className="mt-1 text-xs text-slate-500">Total clocked across all employees</p>
            </Card>
            <Card>
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><Timer className="h-4 w-4 text-gold-dark" aria-hidden /> Timesheet Hours</p>
              <p className="mt-2 font-serif text-3xl text-navy-900" data-testid="report-ts-hours">{data.summary.timesheetHours}h</p>
              <p className="mt-1 text-xs text-slate-500">{data.timesheets.length} entries logged</p>
            </Card>
          </div>

          <p className="mb-3 font-serif text-lg text-navy-900">Clock In / Clock Out</p>
          {data.attendance.length === 0 ? (
            <EmptyState icon={Users} title="No active employees" sub="There are no active employees to report on." testid="report-att-empty" />
          ) : (
            <Card className="!p-0 mb-8 overflow-hidden" testid="attendance-report-card">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="attendance-report-table">
                  <thead>
                    <tr className="border-b border-navy-900/10 bg-ivory text-left">
                      {["Employee", "Role", "Shift", "Status", "In", "Out", "Shift 2", "Hours", "Mode"].map((h) => (
                        <th key={h} className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.attendance.map((r) => (
                      <tr key={r.employeeId} className="border-b border-navy-900/5 last:border-0 hover:bg-ivory/60" data-testid={`att-row-${r.code}`}>
                        <td className="px-5 py-3">
                          <p className="font-semibold text-navy-900">{r.name}</p>
                          <p className="text-[11px] text-slate-400">{r.code} · {r.employmentType}</p>
                        </td>
                        <td className="px-5 py-3 text-slate-600">
                          <p>{r.designation}</p>
                          <p className="text-[11px] text-slate-400">{r.department}</p>
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-500">{r.shiftLabel}</td>
                        <td className="px-5 py-3"><StatusBadge status={r.status} />{r.leaveType && <span className="ml-1 text-[11px] text-slate-400">({r.leaveType})</span>}</td>
                        <td className="px-5 py-3 text-slate-600">{r.checkIn ? fmtTime(r.checkIn) : "—"}</td>
                        <td className="px-5 py-3 text-slate-600">{r.checkOut ? fmtTime(r.checkOut) : "—"}</td>
                        <td className="px-5 py-3 text-slate-600" data-testid={`att-shift2-${r.code}`}>
                          {r.checkIn2 ? `${fmtTime(r.checkIn2)} – ${r.checkOut2 ? fmtTime(r.checkOut2) : "…"}` : "—"}
                        </td>
                        <td className="px-5 py-3 font-semibold text-navy-900">{r.hours ? `${r.hours}h` : "—"}</td>
                        <td className="px-5 py-3 text-slate-600">{r.workMode || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <p className="mb-3 font-serif text-lg text-navy-900">Timesheet Entries</p>
          {data.timesheets.length === 0 ? (
            <EmptyState icon={Timer} title="No timesheet entries" sub="No one has logged work for this date yet." testid="report-ts-empty" />
          ) : (
            <Card className="!p-0 overflow-hidden" testid="timesheet-report-card">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="timesheet-report-table">
                  <thead>
                    <tr className="border-b border-navy-900/10 bg-ivory text-left">
                      {["Employee", "Project", "Task", "Hours", "Billable", "Status"].map((h) => (
                        <th key={h} className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.timesheets.map((t) => (
                      <tr key={t.id} className="border-b border-navy-900/5 last:border-0 hover:bg-ivory/60" data-testid={`ts-row-${t.id}`}>
                        <td className="px-5 py-3">
                          <p className="font-semibold text-navy-900">{t.name}</p>
                          <p className="text-[11px] text-slate-400">{t.code}</p>
                        </td>
                        <td className="px-5 py-3 text-slate-600">{t.project || "—"}</td>
                        <td className="px-5 py-3 text-slate-600">
                          {t.task}
                          {t.description && <p className="mt-0.5 text-xs text-slate-400">{t.description}</p>}
                        </td>
                        <td className="px-5 py-3 font-semibold text-navy-900">{t.hours}h</td>
                        <td className="px-5 py-3 text-slate-600">{t.billable ? "Yes" : "—"}</td>
                        <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

